import React, { Fragment, useEffect } from "react";

import { useAnalytics } from "@common/analytics/useAnalytics";
import * as Icon from "@common/components/DetailsPane/icons";
import { ErrorStateView } from "@common/components/PageStateView/components/ErrorStateView";
import { Spinner } from "@common/components/Spinner/Spinner";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { useCustomElementInjection } from "@common/hooks/useCustomElementInjection";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import NavigationCustomElement from "@main/layouts/components/PageNavigation/NavigationCustomElement/NavigationCustomElement";
import { ELEMENT_CONTAINER_ID, NAV_LIST } from "@main/layouts/components/PageNavigation/PageNavigation";
import { getActivityHistoryHelperText } from "@main/pages/shared/components/ActivityHistoryTable/ActivityHistoryHelpers";
import { ActivityHistoryTable } from "@main/pages/shared/components/ActivityHistoryTable/ActivityHistoryTable";
import { RoutePaths } from "@main/router/config/routePaths";
import { generatePath, useNavigate, useParams } from "react-router-dom";

import * as Styled from "./UserFeedback.styles";
import useUserFeedbackDetailsApiConsumer from "../consumers/useUserFeedbackDetailsApiConsumer";

export default function UserFeedbackActivityHistoryPageView() {
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();
  const { sendEvent } = useAnalytics();

  useEffect(() => {
    sendEvent("page_view", { page_title: "User feedback activity history" });
  }, [sendEvent]);

  const navigate = useNavigate();
  const params = useParams<{
    workspaceSlug: string;
    appKey: string;
    userFeedbackKey: string;
  }>();

  const consumer = useUserFeedbackDetailsApiConsumer({
    selectedWorkspaceId: selectedWorkspace?.id,
    selectedAppId: selectedApp?.id,
    userFeedbackKey: params.userFeedbackKey,
  });

  const CustomElement = consumer.userFeedbackDetails ? (
    <Fragment>
      <NavigationCustomElement>
        <Styled.NavigationTitle>
          <div>
            <p>{consumer.userFeedbackDetails?.title ?? "No description"}</p>
          </div>
        </Styled.NavigationTitle>
        <Styled.NavigationTitle $isButton={false}>
          <Flex>
            <div>
              <Tooltip
                text={"See ticket details"}
                position={"bottom"}
              >
                <Styled.NavbarNavigation
                  onClick={() =>
                    navigate(
                      generatePath(RoutePaths.USER_FEEDBACK_DETAILS, {
                        workspaceSlug: selectedWorkspace?.slug ?? null,
                        appKey: selectedApp?.key ?? null,
                        userFeedbackKey: params.userFeedbackKey ?? null,
                      }),
                    )
                  }
                >
                  <Icon.SendIcon
                    height={20}
                    width={20}
                  />
                </Styled.NavbarNavigation>
              </Tooltip>
            </div>
            <p>Activity history</p>
            <div>
              <Tooltip
                text={!consumer.userFeedbackDetails.log ? "No logs to download" : "Download as .log file"}
                position="bottom"
              >
                <Styled.DownloadIcon
                  disabled={!consumer.userFeedbackDetails.log}
                  onClick={() => {
                    if (consumer.userFeedbackDetails?.log)
                      consumer.downloadActivityHistory(consumer.userFeedbackDetails?.key.toString());
                  }}
                  $disabled={!consumer.userFeedbackDetails.log}
                />
              </Tooltip>
            </div>
          </Flex>
        </Styled.NavigationTitle>
        {getActivityHistoryHelperText()}
      </NavigationCustomElement>
    </Fragment>
  ) : (
    <Fragment />
  );

  useCustomElementInjection({
    parentElementId: NAV_LIST,
    element: CustomElement,
    containerId: ELEMENT_CONTAINER_ID,
    tagName: "li",
  });

  if (consumer.error) {
    return (
      <ErrorStateView
        title="There was an error loading ticket. Try refreshing."
        subtitle="The link you followed may be broken, or the ticket may have been removed."
      />
    );
  }

  if (!params.userFeedbackKey) return <Fragment />;

  if (!consumer.userFeedbackDetails) {
    return <Spinner />;
  }

  return (
    <ActivityHistoryTable
      issueId={consumer.userFeedbackDetails?.id}
      ticketKey={params.userFeedbackKey}
      hasLogs={Boolean(consumer.userFeedbackDetails?.log)}
      type="user_feedback"
    />
  );
}
